"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const _1 = require("../");
class CompModel extends _1.PipelineModel {
    constructor(source, transform) {
        super(source, transform);
        this.instanceType = 'Closed Deal';
        this.archive = (source && source.archive) || false;
    }
    static fromContract(contract) {
        const comp = new CompModel();
        // Following line is to remain commented until Deal Sharing is revived
        // comp.systemCommis = true; // Setting this field to true prevents Salesforce from creating a new Commission for this record
        // Conversion should mirror the createComp() method of ProjectCompController.cls in aptotude/apto-dx/force-app/main/default/classes
        // Common fields
        comp.clientCompany = contract.clientCompany; // Preserved to make sure we pick up legacy data
        comp.clientContact = contract.clientContact; // Preserved to make sure we pick up legacy data
        comp.commissionAmount = contract.commissionAmount;
        comp.contractId = contract._id;
        comp.closeDate = new Date(contract.contractCloseDate);
        comp.listingId = contract.listingId;
        comp.listingType = contract.listingType;
        comp.name = contract.name;
        comp.ownerId = contract.ownerId;
        comp.pursuitId = contract.pursuitId;
        // Sale-specific fields
        comp.buyerCompanyId = contract.buyerCompanyId;
        comp.buyerId = contract.buyerContactId;
        comp.sellerCompanyId = contract.sellerCompanyId;
        comp.sellerId = contract.sellerContactId;
        comp.salePrice = contract.contractPrice;
        // Lease-specific fields
        comp.landlordCompanyId = contract.landlordCompanyId;
        comp.landlordContactId = contract.landlordContactId;
        comp.tenantCompanyId = contract.tenantCompanyId;
        comp.tenantContactId = contract.tenantContactId;
        if (contract.type === _1.ContractModel.TYPES.PENDING_LEASE) {
            comp.type = this.DEAL_TYPES.LEASE;
            if (contract.spacePropertyId) {
                // if we have a space set the leased prop to the parent and the space to the spacePropertyId
                comp.leasedPropertyId = contract.spaceParentPropertyId || null;
                comp.spaceParentPropertyId = comp.leasedPropertyId || null;
                comp.spacePropertyId = contract.spacePropertyId || null;
            }
            else {
                comp.leasedPropertyId = contract.propertyId;
            }
        }
        else {
            comp.type = this.DEAL_TYPES.SALE;
            comp.soldPropertyId = contract.propertyId;
        }
        return comp;
    }
    static transformToDB(comp) {
        return {
            comp_id: comp._id,
            archive: comp.archive
        };
    }
    get isSale() {
        return this.recordType && this.recordType.toLowerCase().includes('sale');
    }
    get isLease() {
        return this.recordType && this.recordType.toLowerCase().includes('lease');
    }
    get salesforceFieldMap() {
        return {
            _id: 'Id',
            askingPrice: 'McLabs2__Asking_Price__c',
            buildingClass: 'McLabs2__Class__c',
            buyerId: 'McLabs2__Buyer__c',
            buyerCompanyId: 'McLabs2__Buyer_Company__c',
            capRate: 'McLabs2__CAP_Rate__c',
            cashOnCash: 'McLabs2__Cash_on_Cash__c',
            closeDate: 'McLabs2__Sale_Date__c',
            commissionAmount: 'McLabs2__Commission__c',
            commissionPercent: 'McLabs2__Commission_Percent__c',
            contractId: 'McLabs2__Escrow__c',
            downPayment: 'McLabs2__Down_Payment__c',
            escalations: 'McLabs2__Escalations__c',
            // TODO: remove grossCommissionAmount after it's reference is updated in apto-mobile (comp-view.html)
            grossCommissionAmount: 'McLabs2__Commission__c',
            landlordCompanyId: 'McLabs2__Landlord__c',
            landlordContactId: 'McLabs2__Landlord_Contact__c',
            lastModifiedDate: 'SystemModstamp',
            leaseCommencementDate: 'McLabs2__Lease_Commencement_Date__c',
            leaseExpirationDate: 'McLabs2__Lease_Expiration_Date__c',
            leaseTermMonths: 'McLabs2__Lease_Term_Months__c',
            leaseTotal: 'McLabs2__Lease_Total__c',
            leaseType: 'McLabs2__Lease_Type__c',
            leasedPropertyId: 'McLabs2__Property_Leases__c',
            listingId: 'McLabs2__Listing__c',
            mortgageAmount: 'McLabs2__Mortgage_Amount__c',
            name: 'Name',
            netOperatingIncome: 'McLabs2__Net_Operating_Income__c',
            occupancyAtClose: 'McLabs2__Occupancy_at_Close__c',
            occupancyDate: 'McLabs2__Occupancy_Date__c',
            options: 'McLabs2__Options__c',
            ownerId: 'OwnerId',
            priceType: 'McLabs2__Price_Type__c',
            pursuitId: 'McLabs2__Proposal__c',
            recordTypeId: 'RecordTypeId',
            rentalRate: 'McLabs2__Rental_Rate__c',
            rentalRateType: 'McLabs2__Rental_Rate_Type__c',
            rentCommencementDate: 'McLabs2__Rent_Commencement_Date__c',
            researchComplete: 'McLabs2__Research_Complete__c',
            salePrice: 'McLabs2__Sale_Price__c',
            sellerId: 'McLabs2__Seller__c',
            sellerCompanyId: 'McLabs2__Seller_Company__c',
            soldPropertyId: 'McLabs2__Property__c',
            spacePropertyId: 'McLabs2__Space_Property__c',
            squareFootage: 'McLabs2__Square_Footage__c',
            sublease: 'McLabs2__Sublease__c',
            subleaseExpirationDate: 'McLabs2__Sublease_Expiration_Date__c ',
            suiteOrFloor: 'McLabs2__Suite_Floor__c',
            // systemCommis: 'McLabs2__System_Commis__c', // This line is to remain commented until Deal Sharing is revived
            tenancyAtClose: 'McLabs2__Tenancy__c',
            tenantCompanyId: 'McLabs2__Tenant__c',
            tenantContactId: 'McLabs2__Tenant_Contact__c',
            termRemainingAtClose: 'McLabs2__Term_Remaining_At_Close__c',
            transactionDate: 'McLabs2__Transaction_Date_v2__c',
            transactionNotes: 'McLabs2__Transaction_Notes__c',
            units: 'McLabs2__Sale_Price_Unit__c'
        };
    }
    transformToSalesforce() {
        const sfComp = super.transformToSalesforce();
        delete sfComp.OwnerId;
        delete sfComp.McLabs2__Class__c;
        delete sfComp.McLabs2__Sale_Price_Unit__c;
        delete sfComp.McLabs2__Term_Remaining_At_Close__c;
        delete sfComp.McLabs2__Tenancy__c;
        delete sfComp.SystemModstamp;
        return sfComp;
    }
    // clientContactId variable deleted; replaced with following getter
    // (Was formerly mapped to incorrect Salesforce field in salesforceFieldMap)
    get clientContactId() {
        if (_1.PursuitModel.TYPES.SELLER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.SELLER_REPRESENTATION) {
            return this.sellerId;
        }
        else if (_1.PursuitModel.TYPES.BUYER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.BUYER_REPRESENTATION) {
            return this.buyerId;
        }
        else if (_1.PursuitModel.TYPES.LANDLORD_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.LANDLORD_REPRESENTATION) {
            return this.landlordContactId;
        }
        else if (_1.PursuitModel.TYPES.TENANT_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.TENANT_REPRESENTATION) {
            return this.tenantContactId;
        }
        else {
            return this.sellerId;
        }
    }
    // clientContactId variable deleted; replaced with following setter
    // (Was formerly mapped to incorrect Salesforce field in salesforceFieldMap)
    set clientContactId(newId) {
        if (_1.PursuitModel.TYPES.SELLER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.SELLER_REPRESENTATION) {
            this.sellerId = newId;
        }
        else if (_1.PursuitModel.TYPES.BUYER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.BUYER_REPRESENTATION) {
            this.buyerId = newId;
        }
        else if (_1.PursuitModel.TYPES.LANDLORD_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.LANDLORD_REPRESENTATION) {
            this.landlordContactId = newId;
        }
        else if (_1.PursuitModel.TYPES.TENANT_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.TENANT_REPRESENTATION) {
            this.tenantContactId = newId;
        }
        else {
            this.sellerId = newId;
        }
    }
    // clientCompanyId variable deleted; replaced with following getter
    // (Was formerly mapped to incorrect Salesforce field in salesforceFieldMap)
    get clientCompanyId() {
        if (_1.PursuitModel.TYPES.SELLER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.SELLER_REPRESENTATION) {
            return this.sellerCompanyId;
        }
        else if (_1.PursuitModel.TYPES.BUYER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.BUYER_REPRESENTATION) {
            return this.buyerCompanyId;
        }
        else if (_1.PursuitModel.TYPES.LANDLORD_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.LANDLORD_REPRESENTATION) {
            return this.landlordCompanyId;
        }
        else if (_1.PursuitModel.TYPES.TENANT_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.TENANT_REPRESENTATION) {
            return this.tenantCompanyId;
        }
        else {
            return this.sellerCompanyId;
        }
    }
    // clientCompanyId variable deleted; replaced with following setter
    // (Was formerly mapped to incorrect Salesforce field in salesforceFieldMap)
    set clientCompanyId(newId) {
        if (_1.PursuitModel.TYPES.SELLER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.SELLER_REPRESENTATION) {
            this.sellerCompanyId = newId;
        }
        else if (_1.PursuitModel.TYPES.BUYER_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.BUYER_REPRESENTATION) {
            this.buyerCompanyId = newId;
        }
        else if (_1.PursuitModel.TYPES.LANDLORD_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.LANDLORD_REPRESENTATION) {
            this.landlordCompanyId = newId;
        }
        else if (_1.PursuitModel.TYPES.TENANT_REPRESENTATION && this.listingType === _1.PursuitModel.TYPES.TENANT_REPRESENTATION) {
            this.tenantCompanyId = newId;
        }
        else {
            this.sellerCompanyId = newId;
        }
    }
    get price() {
        return this.salePrice;
    }
    get propertyId() {
        return this.soldPropertyId;
    }
    set propertyId(id) {
        this.soldPropertyId = id;
    }
    transformFromSalesforce(sfComp) {
        super.transformFromSalesforce(sfComp);
        this.sellerContact = new _1.ContactModel(sfComp.McLabs2__Seller__r, 'transformFromSalesforce');
        this.buyerContact = new _1.ContactModel(sfComp.McLabs2__Buyer__r, 'transformFromSalesforce');
        this.landlordContact = new _1.ContactModel(sfComp.McLabs2__Landlord_Contact__r, 'transformFromSalesforce');
        this.tenantContact = new _1.ContactModel(sfComp.McLabs2__Tenant_Contact__r, 'transformFromSalesforce');
        this.listingType = sfComp.McLabs2__Listing__r && sfComp.McLabs2__Listing__r.RecordType.Name;
        if (sfComp.RecordType && sfComp.RecordType.Name) {
            this.recordType = sfComp.RecordType.Name;
            this.type = this.recordType;
        }
    }
}
CompModel.DEAL_TYPES = {
    SALE: 'Sale',
    LEASE: 'Lease'
};
CompModel.LEASE_TYPES = [
    'N/A',
    'NNN',
    'Modified Gross',
    'Modified Net',
    'Industrial Gross',
    'Other'
];
CompModel.PRICE_TYPES = {
    CONFIRMED: 'Confirmed',
    ESTIMATED: 'Estimated',
    NEEDS_RESEARCH: 'Needs research'
};
CompModel.RENTAL_RATE_TYPES = [
    '$/SF/Year',
    '$/SF/Month',
    'Amount/Year',
    'Amount/Month'
];
exports.CompModel = CompModel;
//# sourceMappingURL=comp.model.js.map